import { useCallback } from 'react';
import { z } from 'zod';

const STORAGE_KEY_PREFIX = 'mercury_z_token_';

const storedZTokenSchema = z.object({
  token: z.string(),
  expires_at: z.string(),
});

type StoredZToken = z.infer<typeof storedZTokenSchema>;

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

export function getStoredZToken(userId: string): string | null {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;

    const parsed = storedZTokenSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      localStorage.removeItem(getStorageKey(userId));
      return null;
    }

    // Check expiration with 60s safety margin
    const expiresAt = new Date(parsed.data.expires_at).getTime();
    const now = Date.now() + 60_000;
    if (now >= expiresAt) {
      localStorage.removeItem(getStorageKey(userId));
      return null;
    }

    return parsed.data.token;
  } catch {
    return null;
  }
}

export function storeZToken(userId: string, token: string, expiresAt: string): void {
  const data: StoredZToken = { token, expires_at: expiresAt };
  localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
}

export function clearZToken(userId: string): void {
  localStorage.removeItem(getStorageKey(userId));
}

export function useZToken() {
  const get = useCallback((userId: string) => getStoredZToken(userId), []);
  const store = useCallback(
    (userId: string, token: string, expiresAt: string) => storeZToken(userId, token, expiresAt),
    []
  );
  const clear = useCallback((userId: string) => clearZToken(userId), []);

  return { getStoredZToken: get, storeZToken: store, clearZToken: clear };
}
